/**
 * KaranaShashtyamshaInfoTable Component
 * Displays D60 Karana Shashtyamsha system for karmic causes analysis
 * 60 divisions: 1 fixed + 56 movable (7×8) + 3 fixed
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack
} from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import {
  calculateKaranaShashtyamshas,
  KaranaShashtyamshaData,
  KaranaShashtyamshaAttributes,
  KARANA_DESCRIPTIONS,
  getKaranaType,
  getCycleNumber
} from '../data/KaranaShashtyamshaTableData';
import {
  getKaranaColor
} from '../../../../../config/karanaShashtyamshaColors';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';
import { createPlanetDisplay } from '../helpers/planetDisplayHelper';
import {
  TABLE_WRAPPER_INLINE_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_ALT_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TD_BASE_STYLES,
  TABLE_TD_SMALL_STYLES,
  TABLE_TD_CENTER_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  EMPTY_CELL_TEXT_STYLES,
  COLUMN_STYLES
} from './styles/tableStyles';

export interface KaranaShashtyamshaInfoTableProps {
  apiResponse?: JatakaResponse | null;
}

/**
 * Group divisions by Karana number for display
 */
interface KaranaRow {
  karanaNumber: number;
  karanaType: any;
  cycleNumber: number | null;
  planets: KaranaShashtyamshaAttributes[];
}

/**
 * Create all 60 Karana rows with planets grouped
 */
function groupByKaranaNumber(divisions: KaranaShashtyamshaAttributes[]): KaranaRow[] {
  const rows: KaranaRow[] = [];
  const planetsByKarana: Record<number, KaranaShashtyamshaAttributes[]> = {};

  // Initialize all 60 Karanas
  for (let i = 1; i <= 60; i++) {
    planetsByKarana[i] = [];
  }

  // Group planets by Karana number
  divisions.forEach(div => {
    planetsByKarana[div.karanaNumber].push(div);
  });

  // Create rows with all info
  for (let i = 1; i <= 60; i++) {
    const planetsInKarana = planetsByKarana[i];
    const karanaType = getKaranaType(i);
    const cycleNumber = getCycleNumber(i);

    rows.push({
      karanaNumber: i,
      karanaType,
      cycleNumber,
      planets: planetsInKarana
    });
  }

  return rows;
}

export const KaranaShashtyamshaInfoTable: React.FC<KaranaShashtyamshaInfoTableProps> = ({ apiResponse }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  // Calculate Karana Shashtyamsha divisions
  const karanaData: KaranaShashtyamshaData = calculateKaranaShashtyamshas(apiResponse);

  if (!karanaData.divisions.length) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>Unable to calculate Karana Shashtyamsha divisions</Text>
      </Box>
    );
  }

  // Group all divisions by Karana number (1-60)
  const karanaRows = groupByKaranaNumber(karanaData.divisions);

  return (
    <Box {...TABLE_WRAPPER_INLINE_STYLES}>
      {/* Karana Shashtyamsha Table */}
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_ALT_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.large}>Planets</Th>
              <Th {...COLUMN_STYLES.xsmall}>Part</Th>
              <Th {...COLUMN_STYLES.medium}>KARANA</Th>
              <Th {...COLUMN_STYLES.small}>GRAHA</Th>
              <Th {...COLUMN_STYLES.medium}>DEVATA</Th>
              <Th {...COLUMN_STYLES.xxlarge}>Karmic Cause</Th>
            </Tr>
          </Thead>
          <Tbody>
            {karanaRows.map((row) => {
              const karanaInfo = KARANA_DESCRIPTIONS[row.karanaType as keyof typeof KARANA_DESCRIPTIONS];
              const karanaColor = getKaranaColor(row.karanaType);

              return (
                <Tr key={row.karanaNumber} _hover={TABLE_ROW_HOVER_STYLES}>
                  {/* Planets column */}
                  <Td {...TABLE_TD_BASE_STYLES} minW="80px">
                    {row.planets.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap">
                        {row.planets.map((division, index) => {
                          // Use centralized planet display helper (DRY)
                          const planetDisplay = createPlanetDisplay(division.planet, currentHoraPlanet);
                          const colors = getAshtamshaPlanetColor(planetDisplay.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                          return (
                            <Text
                              key={`${division.planet.short_name}-${index}`}
                              {...PLANET_TEXT_STYLES}
                              color={colors.color}
                              bg={colors.backgroundColor}
                              border={colors.borderColor ? `1px solid` : 'none'}
                              borderColor={colors.borderColor}
                            >
                              {planetDisplay.displayText}
                            </Text>
                          );
                        })}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>

                  {/* Part Number */}
                  <Td {...TABLE_TD_CENTER_STYLES} fontWeight="normal">
                    <Text fontSize="xs">
                      {row.karanaNumber}
                    </Text>
                  </Td>

                  {/* KARANA with color - use transliteration */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={karanaColor}
                    >
                      {row.karanaType}
                    </Text>
                  </Td>

                  {/* GRAHA */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text fontSize="xs" fontWeight="normal">
                      {karanaInfo.graha}
                    </Text>
                  </Td>

                  {/* DEVATA */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text fontSize="xs" fontWeight="normal">
                      {karanaInfo.devata}
                    </Text>
                  </Td>

                  {/* Karmic Cause */}
                  <Td {...TABLE_TD_SMALL_STYLES} fontWeight="normal">
                    <Text fontSize="xs" color="gray.700">
                      {karanaInfo.meaning}
                    </Text>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
